"use client";

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { User } from "@/types/fleet";
import { loginRequest, logoutRequest, meRequest } from "@/lib/api/auth";
import { ApiClientError } from "@/lib/api/http";
import { flushActivity, setActivityLoggingEnabled } from "@/lib/activity/emit-store";

interface AuthContextType {
  user: User | null;
  /** Same bearer token as used for API auth; for WebSocket query param. Null when logged out. */
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  lastErrorCode: string | null;
  login: (
    username: string,
    password: string,
  ) => Promise<{ ok: boolean; errorCode?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastErrorCode, setLastErrorCode] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const bootstrapSession = async () => {
      try {
        const session = await meRequest();
        if (!isMounted) return;
        setUser(session.user);
        setToken(session.token);
        setActivityLoggingEnabled(true);
      } catch {
        if (!isMounted) return;
        setUser(null);
        setToken(null);
        setActivityLoggingEnabled(false);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<{ ok: boolean; errorCode?: string }> => {
    setLastErrorCode(null);
    try {
      const session = await loginRequest(username, password);
      setUser(session.user);
      setToken(session.token);
      setActivityLoggingEnabled(true);
      return { ok: true };
    } catch (error) {
      setUser(null);
      setToken(null);
      setActivityLoggingEnabled(false);
      if (error instanceof ApiClientError) {
        setLastErrorCode(error.code);
        return { ok: false, errorCode: error.code };
      } else {
        setLastErrorCode("request_failed");
        return { ok: false, errorCode: "request_failed" };
      }
    }
    },
    [],
  );

  const logout = useCallback(() => {
    // Anything still queued belongs to the session that is ending, and the flush needs the cookie
    // that `logoutRequest()` is about to delete — so it goes first, before the state is torn down.
    flushActivity();
    setActivityLoggingEnabled(false);
    setUser(null);
    setToken(null);
    setLastErrorCode(null);
    void logoutRequest();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        lastErrorCode,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
